import type { GetAvailableConsentsResponse, GetUsersConsentsResponse } from "@jti/evo-client";
import { createEvoClient, getAvailableConsents, getUsersConsents, updateConsents } from "@jti/evo-client";
import type {
  Categories,
  OtPurpose,
  OtPurposeByCategory,
  OtPurposeBySteps,
  OtPurposeByUUID,
  OtPurposesByCategoryAndUser,
  OtPurposesByStepsAndUser,
  Steps,
} from "@jti/fe-common";
import { getConfig, getLocale } from "@jti/fe-common";

import { getUser } from "../../../../utils";
import type { CasPurposesApiContract, ConsentPayload, PreparedPurpose } from "../CasPurposesApi.types";

export class CasPurposesApiEvo implements CasPurposesApiContract {
  private readonly client;
  private static instance: CasPurposesApiEvo | null = null;
  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected recaptchaId: string,
    protected ciamToken: string,
    protected userUuid: string,
    protected lang: string
  ) {
    this.client = createEvoClient({
      throwOnError: true,
      baseUrl,
      tenantToken,
      recaptchaId,
      ciamToken,
    });
  }

  static init(): CasPurposesApiEvo {
    const user = getUser();
    const config = getConfig();
    const locale = getLocale();
    const uuid = user.uuid ?? "";

    if (!this.instance || (!this.instance.userUuid && uuid)) {
      this.instance = new CasPurposesApiEvo(
        config.EVO_DOMAIN,
        config.EVO_TENANT,
        config["X-RECAPTCHA-ID"],
        user.ciam_token,
        uuid,
        locale
      );
    }

    return this.instance;
  }

  protected preparePurposesFromEvo<T extends "step" | "category">(
    purposes: GetUsersConsentsResponse | GetAvailableConsentsResponse | undefined,
    contextName: T
  ) {
    if (!purposes) {
      return [];
    }
    return purposes.map(({ context, ...legacyObject }) => ({
      ...legacyObject,
      [contextName]: context,
    })) as unknown as Array<PreparedPurpose<T>>;
  }

  async getPurposesByStep(step: "all"): Promise<OtPurposeBySteps[]>;
  async getPurposesByStep(step: Steps): Promise<OtPurpose[]>;
  async getPurposesByStep(step: Steps): Promise<(OtPurposeBySteps | OtPurpose)[]> {
    const { data } = await getAvailableConsents({
      client: this.client,
      query: {
        context: step,
        language: this.lang,
      },
    });

    return this.preparePurposesFromEvo(data, "step");
  }

  async getPurposesByCategory(category: "all"): Promise<OtPurposeByCategory[]>;
  async getPurposesByCategory(category: Categories): Promise<OtPurpose[]>;
  async getPurposesByCategory(category: Categories): Promise<(OtPurposeByCategory | OtPurpose)[]> {
    const { data } = await getAvailableConsents({
      client: this.client,
      query: {
        context: category,
        language: this.lang,
      },
    });

    return this.preparePurposesFromEvo(data, "category");
  }

  async getPurposesForUserByStep(step: "all"): Promise<OtPurposesByStepsAndUser[]>;
  async getPurposesForUserByStep(step: Steps): Promise<OtPurposeByUUID[]>;
  async getPurposesForUserByStep(step: Steps): Promise<(OtPurposesByStepsAndUser | OtPurposeByUUID)[]> {
    const { data } = await getUsersConsents({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      query: {
        context: step,
        language: this.lang,
      },
    });

    return this.preparePurposesFromEvo(data, "step");
  }

  async getPurposesForUserByCategory(category: "all"): Promise<OtPurposesByCategoryAndUser[]>;
  async getPurposesForUserByCategory(category: Categories): Promise<OtPurposeByUUID[]>;
  async getPurposesForUserByCategory(category: Categories): Promise<(OtPurposesByCategoryAndUser | OtPurposeByUUID)[]> {
    const { data } = await getUsersConsents({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      query: {
        context: category,
        language: this.lang,
      },
    });

    return this.preparePurposesFromEvo(data, "category");
  }

  async updateUserConsentsEvo({ consents }: ConsentPayload): Promise<void> {
    await updateConsents({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: { consents },
    });
  }
}
